// ═══════════════════════════════════════════════════════════════════
//  modules/erp/InvActionModal.js
//  InvActionModal
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";

// Modale d'action staff avec DOUBLE confirmation + suivi (polling 1 s)
export function InvActionModal({ uuid, token, target, onClose, onDone }) {
  // target = { container, slot, item }
  const [step, setStep] = useState("choose"); // choose | confirm | running | done | error
  const [action, setAction] = useState(null); // REMOVE_SLOT | SET_AMOUNT | CLEAR_ALL
  const [amount, setAmount] = useState(1);
  const [errMsg, setErrMsg] = useState("");
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const itemName = target.item ? (target.item.iaId || target.item.id).replace("minecraft:", "").replace(/_/g, " ") : "";
  const containerLabel = target.container === "ENDER" ? "enderchest" : "inventaire";

  const recap = action === "CLEAR_ALL"
    ? `Vider entièrement le ${containerLabel} de ce joueur`
    : action === "REMOVE_SLOT"
      ? `Retirer « ${itemName} » (slot ${target.slot}) du ${containerLabel}`
      : `Mettre « ${itemName} » (slot ${target.slot}) à ${amount} exemplaire(s)`;

  const submit = async () => {
    setStep("running"); setErrMsg("");
    try {
      const body = { container: target.container, action };
      if (action !== "CLEAR_ALL") body.slot = target.slot;
      if (action === "SET_AMOUNT") body.amount = amount;
      const res = await fetch(`${API_BASE}/api/players/${uuid}/inventory/actions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setErrMsg(data.error || "Erreur"); setStep("error"); return; }
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries++;
        try {
          const r = await fetch(`${API_BASE}/api/players/${uuid}/inventory/actions?id=${data.id}`, { headers: { Authorization: `Bearer ${token}` } });
          const rowData = await r.json();
          if (rowData && (rowData.status === "done" || rowData.status === "error")) {
            clearInterval(pollRef.current);
            if (rowData.status === "done") { setStep("done"); onDone && onDone(); }
            else { setErrMsg("Le plugin a renvoyé une erreur."); setStep("error"); }
          } else if (tries >= 30) {
            clearInterval(pollRef.current);
            setErrMsg("Toujours en cours après 30 s — réessaie plus tard."); setStep("error");
          }
        } catch { /* on retente au prochain tick */ }
      }, 1000);
    } catch { setErrMsg("Impossible de contacter le serveur"); setStep("error"); }
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
  const box = { background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 22, width: 380, maxWidth: "90vw" };
  const btn = (bg, bd, col) => ({ background: bg, border: `1px solid ${bd}`, color: col, borderRadius: 6, padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700 });

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget && step !== "running") onClose(); }}>
      <div style={box}>
        <div style={{ color: THEME.text, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          {target.container === "ENDER" ? "Action — Enderchest" : "Action — Inventaire"}
        </div>
        <div style={{ color: THEME.textMuted, fontSize: 12, marginBottom: 16 }}>
          {target.item ? `${itemName} ×${target.item.count} · slot ${target.slot}` : `slot ${target.slot}`}
        </div>

        {step === "choose" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button style={btn(THEME.redDim, THEME.red, THEME.red)} onClick={() => { setAction("REMOVE_SLOT"); setStep("confirm"); }}>🗑️ Vider ce slot</button>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="number" min={0} value={amount} onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
                style={{ width: 70, background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "8px 10px", color: THEME.text, fontSize: 13 }} />
              <button style={{ ...btn(THEME.amberDim, THEME.amber, THEME.amber), flex: 1 }} onClick={() => { setAction("SET_AMOUNT"); setStep("confirm"); }}>📦 Définir la quantité</button>
            </div>
            <button style={btn(THEME.redDim, THEME.red, THEME.red)} onClick={() => { setAction("CLEAR_ALL"); setStep("confirm"); }}>💥 Tout vider ({containerLabel})</button>
            <button style={btn("transparent", THEME.border, THEME.textMuted)} onClick={onClose}>Annuler</button>
          </div>
        )}

        {step === "confirm" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ color: THEME.text, fontSize: 13, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "10px 12px" }}>
              ⚠️ Confirmation requise<br />{recap}.
            </div>
            <div style={{ color: THEME.textMuted, fontSize: 12 }}>Cette action est irréversible. Confirme une seconde fois pour l'exécuter.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={{ ...btn("transparent", THEME.border, THEME.textMuted), flex: 1 }} onClick={() => setStep("choose")}>← Retour</button>
              <button style={{ ...btn(THEME.red, THEME.red, "#FFFFFF"), flex: 1 }} onClick={submit}>Confirmer le retrait</button>
            </div>
          </div>
        )}

        {step === "running" && (
          <div style={{ color: THEME.textMuted, fontSize: 13, textAlign: "center", padding: 12 }}>⏳ Action en cours… (le plugin traite la demande)</div>
        )}

        {step === "done" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ color: THEME.emerald, fontSize: 14, textAlign: "center" }}>✅ Action effectuée</div>
            <button style={btn(THEME.accentDim, THEME.accent, THEME.accent)} onClick={onClose}>Fermer</button>
          </div>
        )}

        {step === "error" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ color: THEME.red, fontSize: 13, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "10px 12px" }}>{errMsg}</div>
            <button style={btn("transparent", THEME.border, THEME.textMuted)} onClick={onClose}>Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}
